import React, {useRef, useState, useEffect, useMemo} from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
// Remove OrbitControls if no longer needed for debugging
// import { OrbitControls, Sky, Stars } from '@react-three/drei';
import { Sky, Stars } from '@react-three/drei';
import { useLoader } from '@react-three/fiber';
import { TextureLoader } from 'three';
// Import the wall texture directly
import wallTexture from '../assets/wall.jpeg';
import * as THREE from 'three';
import styled from 'styled-components';
import VegetableCharacterFactory from './characters/VegetableCharacterFactory';
import {Clouds} from "./Clouds.jsx";
import {Fireflies} from "./Fireflies.jsx";
import {Flare} from "./Flare.jsx";
import {Wall} from "./Wall.jsx";
import {Tree} from "./Tree.jsx";
import {Plant} from "./Plant.jsx";
// Add this import with your other texture imports
import grassTexture from '../assets/grasslight-small.jpg';
import FpsCounter, {FpsMonitor} from "./FpsCounter.jsx";
import {MAX_ZOOM, MIN_ZOOM, vegetableColors} from "./constants.js";
import OtherPlayer from "./characters/OtherPlayer.jsx";
import { useWebSocket } from '../contexts/WebSocketContext'; // NEW: for server time
import Diamond from './Diamond.jsx'; // NEW: diamonds
import PickupEffects from './PickupEffects.jsx'; // NEW: pickup animations

const CanvasContainer = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 0; /* Change from -1 to 0 */
`;

// Create a context for day/night cycle
const DayNightContext = React.createContext({ time: 0.3, isDay: true });

// --- DayNightCycle component - Modified ---
// Accept props for initial time and duration
function DayNightCycle({ children, dayLengthMinutes = 15, initialTimeOfDay = 0.3, cycleEpochMs = 0 }) {
    const { getServerTime } = useWebSocket();
    // Initialize state with props
    const [time, setTime] = useState(initialTimeOfDay);
    const [isPaused, setIsPaused] = useState(false);

    // Reference to keep track of actual elapsed time and duration
    const timeRef = useRef({
        startTime: Date.now(),
        lastUpdateTime: Date.now(),
        // Use prop for day length
        dayLengthMs: dayLengthMinutes * 60 * 1000,
    });

    // Update time based on server time if available, else fallback to real elapsed time
    useFrame(() => {
        if (isPaused) return;

        const dayLengthMs = timeRef.current.dayLengthMs;

        if (cycleEpochMs && cycleEpochMs > 0 && typeof getServerTime === 'function') {
            const nowServer = getServerTime();
            const t = (initialTimeOfDay + ((nowServer - cycleEpochMs) / dayLengthMs)) % 1;
            // Ensure positive modulo
            const normalized = t < 0 ? (t + 1) % 1 : t;
            setTime(normalized);
        } else {
            // Fallback: local progression
            const now = Date.now();
            const elapsed = now - timeRef.current.lastUpdateTime;
            timeRef.current.lastUpdateTime = now;
            const timeIncrement = elapsed / dayLengthMs;
            setTime(prevTime => (prevTime + timeIncrement) % 1);
        }
    });

    // Allow changing day length dynamically if prop changes (optional)
    useEffect(() => {
        timeRef.current.dayLengthMs = dayLengthMinutes * 60 * 1000;
    }, [dayLengthMinutes]);

    // Reset time if initialTimeOfDay prop changes (e.g., on reconnect/full state update)
    useEffect(() => {
        setTime(initialTimeOfDay);
        timeRef.current.lastUpdateTime = Date.now(); // Reset last update time too
    }, [initialTimeOfDay]);


    // --- All the calculation useMemos (sunPosition, moonPosition, dayNightStatus, etc.) remain the same ---
    // ... (sunPosition calculation) ...
    const sunPosition = useMemo(() => {
        const adjustedTime = (time + 0.75) % 1;
        const angle = adjustedTime * Math.PI * 2;
        const radius = 20;
        const x = Math.cos(angle) * radius;
        const y = Math.sin(angle) * radius;
        return [x, y, 0];
    }, [time]);
    // ... (moonPosition calculation) ...
    const moonPosition = useMemo(() => {
        const [x, y, z] = sunPosition;
        return [-x, -y, z];
    }, [sunPosition]);
    // ... (dayNightStatus calculation) ...
    const dayNightStatus = useMemo(() => {
        const dawnStart = 0.20, sunrise = 0.25, sunset = 0.75, duskEnd = 0.80;
        let isTwilight = false, twilightProgress = 0, isDay = false;
        if (time > sunrise && time < sunset) { isDay = true; twilightProgress = 1; }
        else if (time >= dawnStart && time <= sunrise) { isTwilight = true; twilightProgress = (time - dawnStart) / (sunrise - dawnStart); }
        else if (time >= sunset && time <= duskEnd) { isTwilight = true; twilightProgress = 1 - ((time - sunset) / (duskEnd - sunset)); }
        else { twilightProgress = 0; }
        return { isDay, isTwilight, twilightProgress };
    }, [time]);
    // ... (contextValue calculation) ...
    const contextValue = useMemo(() => ({ time, ...dayNightStatus }), [time, dayNightStatus]);
    // ... (starsOpacity calculation) ...
    const starsOpacity = useMemo(() => {
        if (dayNightStatus.isDay) return 0;
        if (dayNightStatus.isTwilight) return 1 - dayNightStatus.twilightProgress;
        return 1;
    }, [dayNightStatus]);
    // ... (ambientLightIntensity calculation) ...
    const ambientLightIntensity = useMemo(() => {
        if (dayNightStatus.isDay) { const dayTime = time < 0.5 ? (time - 0.25) * 4 : (0.75 - time) * 4; return 0.3 + (dayTime * 0.2); }
        if (dayNightStatus.isTwilight) { return 0.15 + (dayNightStatus.twilightProgress * 0.15); }
        return 0.08;
    }, [time, dayNightStatus]);
    // ... (directionalLight calculation) ...
    const directionalLight = useMemo(() => {
        let intensity, color;
        const sunriseColor = new THREE.Color('#ff9e7d');
        const noonColor = new THREE.Color('#ffffff');
        const moonlightColor = new THREE.Color('#b0c4de');
        if (dayNightStatus.isDay) {
            const dayProgress = time < 0.5 ? (time - 0.25) * 4 : (0.75 - time) * 4;
            intensity = 0.5 + (dayProgress * 0.5);
            color = sunriseColor.clone().lerp(noonColor, dayProgress);
        } else if (dayNightStatus.isTwilight) {
            intensity = 0.3 + (dayNightStatus.twilightProgress * 0.2);
            color = moonlightColor.clone().lerp(sunriseColor, dayNightStatus.twilightProgress);
        } else {
            intensity = 0.15;
            color = moonlightColor;
        }
        return { intensity, color: color.getHexString() };
    }, [time, dayNightStatus]);
    // ... (celestialBodies calculation) ...
    const celestialBodies = useMemo(() => {
        const sunHeight = sunPosition[1], moonHeight = moonPosition[1];
        let sunVisibility = 0, moonVisibility = 0;
        if (sunHeight > 0) sunVisibility = Math.min(1, sunHeight / 5);
        if (moonHeight > 0) moonVisibility = Math.min(1, moonHeight / 5);
        if (dayNightStatus.isTwilight) {
            if (time < 0.5) { sunVisibility = dayNightStatus.twilightProgress; moonVisibility = Math.max(0, 1 - dayNightStatus.twilightProgress * 1.5); }
            else { sunVisibility = dayNightStatus.twilightProgress; moonVisibility = Math.max(0, 1 - dayNightStatus.twilightProgress * 0.8); }
        }
        if (!dayNightStatus.isDay && !dayNightStatus.isTwilight) sunVisibility = 0;
        return { sunVisibility, moonVisibility, sunHeight };
    }, [sunPosition, moonPosition, dayNightStatus, time]);
    // ... (debug logging useEffect - keep if useful) ...
    // const logTimeKey = Math.floor(time * 100);
    // useEffect(() => { if (time > 0.7 || time < 0.3) { console.log(...) } }, [logTimeKey, celestialBodies.sunVisibility > 0.05]);


    // Pass time and related values to children
    return (
        <DayNightContext.Provider value={contextValue}>
            {/* Sky - always present with varying opacity */}
            <Sky
                distance={450000}
                sunPosition={sunPosition}
                inclination={0}
                azimuth={0.25}
                rayleigh={dayNightStatus.isDay ? 1 : 0.2 + (dayNightStatus.twilightProgress * 0.8)}
                turbidity={dayNightStatus.isDay ? 10 : 5}
                mieCoefficient={dayNightStatus.isDay ? 0.005 : 0.002}
                mieDirectionalG={0.8}
            />

            {/* Stars - always present with varying opacity */}
            <Stars
                radius={100}
                depth={50}
                count={1000}
                factor={4}
                saturation={0}
                fade
                speed={0.5}
                opacity={starsOpacity}
            />

            {/* Clouds */}
            {/*<Clouds time={time} isDay={dayNightStatus.isDay || dayNightStatus.twilightProgress > 0.5} />*/}

            {/* Fireflies - add them before sun/moon so they don't appear in front */}
            <Fireflies time={time} />

            {/* Sun with varying opacity */}
            <group position={sunPosition} visible={celestialBodies.sunVisibility > 0}>
                {/* The sun itself - a glowing sphere */}
                <mesh>
                    <sphereGeometry args={[2, 32, 32]} />
                    <meshBasicMaterial color="#fff9c4" opacity={Math.max(0.1, celestialBodies.sunVisibility)} transparent={true} />
                </mesh>
                {celestialBodies.sunVisibility > 0.05 && celestialBodies.sunHeight > 0 && <Flare position={[0, 0, 0]} />}
            </group>
            {/* Moon group */}
            <group position={moonPosition} visible={celestialBodies.moonVisibility > 0}>
                 <mesh>
                    <sphereGeometry args={[1, 16, 16]} />
                    <meshBasicMaterial color="#f0f0ff" opacity={celestialBodies.moonVisibility} transparent={true} />
                </mesh>
                <pointLight distance={60} intensity={0.8 * celestialBodies.moonVisibility} color="#b0c4de" />
            </group>
            {/* Lights */}
            <ambientLight intensity={ambientLightIntensity} />
            <directionalLight
                position={sunPosition[1] > moonPosition[1] ? sunPosition : moonPosition}
                intensity={directionalLight.intensity}
                color={`#${directionalLight.color}`}
                castShadow
                shadow-mapSize={[2048, 2048]}
                shadow-camera-left={-30}
                shadow-camera-right={30}
                shadow-camera-top={30}
                shadow-camera-bottom={-30}
                shadow-camera-near={0.5}
                shadow-camera-far={50}
            />

            {/* Children */}
            {children}
        </DayNightContext.Provider>
    );
}


// --- Ground component remains unchanged ---
function Ground({simple=false}) {
    const texture = useLoader(TextureLoader, grassTexture);
    texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
    const gardenSize = 30;
    texture.repeat.set(8, 8);

    if (simple) {
        return (
            <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]} receiveShadow>
                <planeGeometry args={[30, 30]} />
                <meshStandardMaterial color="#4b7f3f" roughness={0.8} metalness={0.2} />
            </mesh>
        );
    }
    return (
        <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]} receiveShadow>
            <planeGeometry args={[gardenSize, gardenSize]} />
            <meshStandardMaterial map={texture} roughness={0.8} metalness={0.2} />
        </mesh>
    );
}




// --- VegetablePlayer component - Modified ---
// Accept plants, walls, tree, invertY as props
function VegetablePlayer({ vegetableType, controlsRef, onCharacterGrow, plants = [], walls = [], tree = null, invertY }) {
    const vegetableMeshRef = useRef();
    const [position, setPosition] = useState([0, 1, 0]);
    const [zoomMultiplier, setZoomMultiplier] = useState(1.0);
    const [characterRotation, setCharacterRotation] = useState([0, 0, 0]);
    const [cameraYaw, setCameraYaw] = useState(0);
    const [cameraPitch, setCameraPitch] = useState(0);
    const [targetRotation, setTargetRotation] = useState([0, 0, 0]);
    const [velocity, setVelocity] = useState([0, 0, 0]);
    const { camera } = useThree();
    const { diamonds, sendData, computeCurrentGlow } = useWebSocket(); // NEW
    const pickupCooldownRef = useRef({}); // prevent spamming collect
    const color = vegetableColors[vegetableType] || vegetableColors.default;
    const isWalking = velocity[0] !== 0 || velocity[2] !== 0;
    const [scale, setScale] = useState(0.25);
    const maxScale = 1.5; // Maximum growth
    const growthIncrement = 0.15; // How much to grow each time

    // Add debug state to visualize collisions
    const [debug, setDebug] = useState({
        colliding: false,
        collisionPoint: null,
        plantRadius: null // Keep for debug visualization
    });

    // REMOVE useEffect hooks for loading/polling plants, walls, tree
    // useEffect(() => { /* loadPlants logic */ }, []); // REMOVE
    // useEffect(() => { /* loadWalls logic */ }, []);  // REMOVE
    // useEffect(() => { /* loadTree logic */ }, []);   // REMOVE

    // Add a growth notification handler to controlsRef
    // This useEffect remains, but doesn't depend on internal state anymore
    useEffect(() => {
        if (controlsRef?.current) {
            controlsRef.current.onGrowth = () => {
                if (scale < maxScale) {
                    const newScale = Math.min(scale + growthIncrement, maxScale);
                    setScale(newScale);
                    console.log(`Character has grown to scale: ${newScale.toFixed(2)}`);
                    // Call the onCharacterGrow callback passed down via props
                    if (onCharacterGrow) { // Check if the prop exists
                        onCharacterGrow(newScale, maxScale);
                    }
                    return true;
                }
                return false;
            };
        }
        // Cleanup function
        return () => {
            if (controlsRef?.current) {
                controlsRef.current.onGrowth = null;
            }
        };
    // Pass onCharacterGrow in dependency array if it might change, otherwise keep it simple
    }, [controlsRef, scale, maxScale, growthIncrement, onCharacterGrow]);


    // Track latest state values (including invertY)
    const playerState = useRef({
        position: position,
        characterRotation: characterRotation,
        targetRotation: targetRotation,
        cameraYaw: cameraYaw,
        cameraPitch: cameraPitch,
        velocity: velocity,
        zoomMultiplier: zoomMultiplier,
        invertY: invertY, // Track invertY setting
    });

    // Update ref when state changes
    useEffect(() => {
        playerState.current.position = position;
        playerState.current.characterRotation = characterRotation;
        playerState.current.targetRotation = targetRotation;
        playerState.current.cameraYaw = cameraYaw;
        playerState.current.cameraPitch = cameraPitch;
        playerState.current.velocity = velocity;
        playerState.current.zoomMultiplier = zoomMultiplier;
        playerState.current.invertY = invertY; // Update invertY in ref
    }, [position, characterRotation, targetRotation, cameraYaw, cameraPitch, velocity, zoomMultiplier, invertY]); // Add invertY dependency


    // Set up controls once on mount
    useEffect(() => {
        console.log("Setting up controls...");
        if (!controlsRef.current) controlsRef.current = {}; // Ensure ref is initialized

        // --- ROTATION handler - Apply inversion here ---
        controlsRef.current.rotate = (deltaX, deltaY) => {
            // Access invertY from the ref to get the latest value inside this closure
            const currentInvertY = playerState.current.invertY;
            const inversionFactor = currentInvertY ? -1 : 1; // -1 if inverted, 1 if normal

            // Update camera yaw (X-axis rotation - usually not inverted)
            setCameraYaw(prev => prev - deltaX);

            // Update camera pitch (Y-axis rotation - apply inversion)
            setCameraPitch(prev => {
                // Apply inversion factor to deltaY
                const adjustedDeltaY = deltaY * inversionFactor;
                return Math.max(
                    -Math.PI / 2.5,
                    Math.min(Math.PI / 2.5, prev - adjustedDeltaY) // Use adjustedDeltaY
                );
            });
        };

        // Provide a way to apply resume state from server
        controlsRef.current.setPlayerState = (state) => {
            try {
                const pos = Array.isArray(state?.position) && state.position.length >= 3
                    ? state.position
                    : [0, 1, 0];
                const rot = Array.isArray(state?.rotation) && state.rotation.length >= 3
                    ? state.rotation
                    : [0, 0, 0];
                const scl = (typeof state?.scale === 'number' && !Number.isNaN(state.scale))
                    ? state.scale
                    : 0.25;

                // Apply position (keep y at least 1.0)
                setPosition([pos[0], Math.max(1, pos[1] ?? 1), pos[2]]);
                // Apply rotation (use yaw component)
                const yaw = rot[1] || 0;
                setCharacterRotation([0, yaw, 0]);
                setTargetRotation([0, yaw, 0]);
                setCameraYaw(yaw);
                // Reset camera pitch to a neutral angle
                setCameraPitch(0);
                // Apply scale
                setScale(scl);
                // Reset velocity
                setVelocity([0, 0, 0]);
            } catch (e) {
                console.error('Failed to apply resume player state:', e, state);
            }
        };

        // --- MOVEMENT handler ---
        controlsRef.current.move = (moveX, moveZ, jump) => {
            const currentCameraYaw = playerState.current.cameraYaw;
            const currentVelocityY = playerState.current.velocity[1];
            const currentPositionY = playerState.current.position[1];
            const forward = new THREE.Vector3(Math.sin(currentCameraYaw), 0, Math.cos(currentCameraYaw));
            const right = new THREE.Vector3(Math.sin(currentCameraYaw + Math.PI/2), 0, Math.cos(currentCameraYaw + Math.PI/2));
            const moveVector = new THREE.Vector3();
            moveVector.addScaledVector(forward, -moveZ);
            moveVector.addScaledVector(right, -moveX);

            if (moveVector.lengthSq() > 0) {
                moveVector.normalize();

                // Calculate speed based on scale
                // Base speed is 2, scales with character size
                // You can adjust the formula to balance gameplay
                const baseSpeed = 2;
                const speedMultiplier = Math.min(2.5, Math.max(1, scale)); // Clamp between 1x and 2.5x
                const finalSpeed = baseSpeed * speedMultiplier;

                moveVector.multiplyScalar(finalSpeed);
                setVelocity([moveVector.x, currentVelocityY, moveVector.z]);

                // IMPORTANT: Update character rotation to face movement direction
                // Only when actually moving
                // Instead of immediately setting character rotation,
                // set the target rotation that we'll smoothly rotate towards
                const targetYaw = Math.atan2(moveVector.x, moveVector.z);
                setTargetRotation(prev => [prev[0], targetYaw, 0]);
            } else {
                // No movement, maintain vertical velocity but stop horizontal
                setVelocity([0, currentVelocityY, 0]);
            }

            // Handle jumping
            if (jump > 0 && Math.abs(currentPositionY - 1) < 0.01 && currentVelocityY <= 0) {
                // Optionally make jump height scale with character size
                const jumpForce = 5; // Base jump force
                setVelocity(prev => [prev[0], jumpForce, prev[2]]);
            }
        };

        // --- ZOOM handler ---
        controlsRef.current.onZoom = (delta) => {
            // delta will be negative for zoom in, positive for zoom out
            setZoomMultiplier(prev => {
                const newZoom = prev - delta; // Apply the change
                // Clamp the zoom level within limits
                return Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, newZoom));
            });
        };

        // --- GROWTH handler (ensure it's correctly passed) ---
        // This is handled by the separate useEffect for onGrowth now

        // Cleanup function
        return () => {
            console.log("Cleaning up controls reference functions");
            if (controlsRef.current) {
                // Nullify setPlayerState to avoid stale closures on unmount/recreate
                controlsRef.current.setPlayerState = null;
            }
            // Optional: Nullify other functions if needed
            // if (controlsRef.current) {
            //     controlsRef.current.rotate = null;
            //     controlsRef.current.move = null;
            //     controlsRef.current.onZoom = null;
            // }
        };
    // Add scale dependency because move speed depends on it
    }, [controlsRef, scale]); // Removed onCharacterGrow, handled in separate effect


    // Physics & camera update - Use props for collision
    useFrame((state, delta) => {
        if (!vegetableMeshRef.current) return;

        // --- Physics Update ---
        // Apply gravity if in the air
        if (position[1] > 1) {
            setVelocity(prev => [prev[0], prev[1] - 9.8 * delta, prev[2]]);
        }

        // Calculate potential new position
        const newPosition = [
            position[0] + velocity[0] * delta,
            position[1] + velocity[1] * delta,
            position[2] + velocity[2] * delta
        ];

        // Ground collision/snap
        if (newPosition[1] < 1) {
            newPosition[1] = 1;
            setVelocity(prev => [prev[0], 0, prev[2]]);
        }

        // Enhanced collision detection with resolution
        let hasCollision = false;
        let collisionPoint = null;
        let detectedPlantRadius = null; // For debug
        let pushVector = [0, 0, 0];
        const collisionRadius = 0.3;
        const plantCollisionPadding = 0.2;
        const playerRadius = 0.3 * scale;

        // Plant collision - Use 'plants' prop
        if (plants && plants.length > 0) {
            plants.forEach(plant => {
                // Check if plant data structure matches (e.g., plant.Position vs plant.position)
                // Assuming backend sends { Position: [x, y, z], Scale: s, ... }
                const plantPos = plant.Position || plant.position; // Adapt to backend structure
                const plantScale = plant.Scale || plant.scale;     // Adapt to backend structure
                if (!plantPos) {
                    console.error("Invalid plant data:", plant);
                    return;
                }

                const dx = newPosition[0] - plantPos[0];
                const dz = newPosition[2] - plantPos[2]; // Backend Y is often up, Z is depth
                const distanceSq = dx * dx + dz * dz;
                const plantRadius = (0.2 * plantScale) + plantCollisionPadding;
                const totalRadius = collisionRadius + plantRadius;

                if (distanceSq < totalRadius * totalRadius) {
                    hasCollision = true;
                    collisionPoint = plantPos;
                    detectedPlantRadius = plantRadius; // For debug
                    // console.log("Collision with plant at", collisionPoint); // Reduce logging noise

                    const distance = Math.sqrt(distanceSq);
                    if (distance > 0.01) {
                        const pushDistance = totalRadius - distance + 0.05;
                        const pushScale = pushDistance / distance;
                        pushVector[0] += dx * pushScale;
                        pushVector[2] += dz * pushScale;
                    }
                }
            });
        } else {
            console.warn("No plants loaded for collision detection");
        }

        // Wall collision - Use 'walls' prop
        if (!hasCollision && walls && walls.length > 0) {
            walls.forEach(wall => {
                // Assuming backend sends { Position: [x, y, z], Size: [w, h, d], ... }
                const wallPos = wall.Position || wall.position;
                const wallSize = wall.Size || wall.size;
                 if (!wallPos || !wallSize) {
                    console.error("Invalid wall data:", wall);
                    return;
                }

                const halfWidth = wallSize[0] / 2;
                const halfHeight = wallSize[1] / 2;
                const halfDepth = wallSize[2] / 2;
                const minX = wallPos[0] - halfWidth, maxX = wallPos[0] + halfWidth;
                const minY = wallPos[1] - halfHeight, maxY = wallPos[1] + halfHeight;
                const minZ = wallPos[2] - halfDepth, maxZ = wallPos[2] + halfDepth; // Backend Z is depth

                const px = newPosition[0], py = newPosition[1], pz = newPosition[2];
                const closestX = Math.max(minX, Math.min(px, maxX));
                const closestY = Math.max(minY, Math.min(py, maxY));
                const closestZ = Math.max(minZ, Math.min(pz, maxZ));
                const distanceX = px - closestX, distanceY = py - closestY, distanceZ = pz - closestZ;
                const distanceSquared = distanceX * distanceX + distanceY * distanceY + distanceZ * distanceZ;

                if (distanceSquared < playerRadius * playerRadius) {
                    hasCollision = true;
                    // console.log("Wall collision detected"); // Reduce logging noise

                    const distance = Math.sqrt(distanceSquared);
                    if (distance > 0.01) {
                        const pushDistance = playerRadius - distance + 0.05;
                        const pushScale = pushDistance / distance;
                        pushVector[0] += distanceX * pushScale;
                        pushVector[1] += distanceY * pushScale; // Include Y push for walls
                        pushVector[2] += distanceZ * pushScale;
                    }
                    // Update debug info if needed
                    // setDebug({ colliding: true, collisionPoint: [closestX, closestY, closestZ] });
                }
            });
        }

        // Tree collision - Use 'tree' prop
        if (!hasCollision && tree) {
             // Assuming backend sends { Position: [x, y, z], Radius: r }
            const treePos = tree.Position || tree.position;
            const treeRadius = tree.Radius || tree.radius;
             if (!treePos || treeRadius === undefined) {
                console.error("Invalid tree data:", tree);
             } else {
                const dx = newPosition[0] - treePos[0];
                const dz = newPosition[2] - treePos[2]; // Backend Z is depth
                const distanceSq = dx * dx + dz * dz;
                const totalRadius = playerRadius + treeRadius;

                if (distanceSq < totalRadius * totalRadius) {
                    hasCollision = true;
                    collisionPoint = treePos; // For debug
                    // console.log("Tree collision detected"); // Reduce logging noise

                    const distance = Math.sqrt(distanceSq);
                    if (distance > 0.01) {
                        const pushDistance = totalRadius - distance + 0.05;
                        const pushScale = pushDistance / distance;
                        pushVector[0] += dx * pushScale;
                        pushVector[2] += dz * pushScale;
                    }
                }
            }
        }

        // Update debug info
        setDebug({ colliding: hasCollision, collisionPoint: collisionPoint, plantRadius: detectedPlantRadius });

        // --- Collision Resolution --- (No changes needed here)
        if (hasCollision) {
            const pushLengthSq = pushVector[0]**2 + pushVector[1]**2 + pushVector[2]**2;
            if (pushLengthSq > 0.0001) {
                const pushLength = Math.sqrt(pushLengthSq);
                const normalizedPush = [ pushVector[0] / pushLength, pushVector[1] / pushLength, pushVector[2] / pushLength ];
                const resolvedPosition = [
                    position[0] + normalizedPush[0] * 0.1,
                    position[1] + normalizedPush[1] * 0.1,
                    position[2] + normalizedPush[2] * 0.1
                ];
                setPosition(resolvedPosition);
                // Stop movement partially based on collision normal? Or just stop horizontal?
                // Simple stop:
                setVelocity(prev => [0, prev[1] > 0 ? prev[1] : 0, 0]); // Stop horizontal, allow falling/jumping up
                return; // Skip rest of frame update
            }
        }

                // Update position if no collision or resolution
        if (!hasCollision) { // This condition might need refinement based on resolution logic
            setPosition(newPosition);
        }
        // If collision happened but pushVector was zero (e.g., stuck), maybe just stop velocity
        else if (pushVector[0] === 0 && pushVector[1] === 0 && pushVector[2] === 0) {
             setVelocity(prev => [0, prev[1] > 0 ? prev[1] : 0, 0]);
        }


        // Smoothly rotate character towards target rotation
        if (Math.abs(characterRotation[1] - targetRotation[1]) > 0.01) {
            setCharacterRotation(prev => [
                prev[0],
                THREE.MathUtils.lerp(
                    prev[1],
                    targetRotation[1],
                    0.2 // Turning speed
                ),
                prev[2]
            ]);
        }

        // Update mesh orientation - using character rotation
        // The invisible mesh used for physics/camera anchor
        vegetableMeshRef.current.position.set(...position);
        vegetableMeshRef.current.rotation.set(0, characterRotation[1], 0);

        // Apply scale to the actual visible character model via the factory component
        // (This is handled by passing `scale` prop to VegetableCharacterFactory below)

        // Apply camera pitch to eyes (if the model has an 'eyesGroup')
        // This is also handled by passing `lookPitch` to VegetableCharacterFactory below

        // --- Camera Positioning ---
        // Position camera based on CAMERA yaw, pitch, zoom, and player position
        const baseCameraDistance = 5 + (scale - 1) * 1.5; // Base distance scales slightly with player size
        const cameraDistance = baseCameraDistance * zoomMultiplier; // Apply zoom
        const cameraHeight = 2; // Base height offset

        const targetCameraOffset = new THREE.Vector3(0, cameraHeight, -cameraDistance); // Start behind
        // Rotate around Y axis (yaw)
        targetCameraOffset.applyAxisAngle(new THREE.Vector3(0, 1, 0), cameraYaw);

        // Apply pitch rotation around the horizontal axis perpendicular to camera direction
        const horizontalAxis = new THREE.Vector3(Math.cos(cameraYaw), 0, -Math.sin(cameraYaw)).normalize();
        const pitchFactor = 0.7; // Reduce the impact of pitch on camera position offset
        targetCameraOffset.applyAxisAngle(horizontalAxis, cameraPitch * pitchFactor);

        // Calculate final target camera position
        const targetCameraPosition = new THREE.Vector3(...position).add(targetCameraOffset);

        // Adjust lookAt target height based on camera pitch
        let lookAtHeight = 0.5 + Math.sin(cameraPitch) * 1.0; // Aim higher/lower based on pitch

        // CONSTRAIN: Prevent camera from going below ground level
        const minCameraHeight = 0.5;
        if (targetCameraPosition.y < minCameraHeight) {
            targetCameraPosition.y = minCameraHeight;
            lookAtHeight = Math.max(lookAtHeight, 0.2); // Adjust lookAt when constrained
        }

        // Smoothly move camera towards the target position
        camera.position.lerp(targetCameraPosition, 0.1);

        // Set camera lookAt target
        const lookAtPosition = new THREE.Vector3(position[0], position[1] + lookAtHeight, position[2]);
        camera.lookAt(lookAtPosition);

        // Update player state in GameScreen via controlsRef
        if (controlsRef.current && typeof controlsRef.current.updatePlayerState === 'function') {
            const data = {
                position: position,
                characterRotation: characterRotation,
                velocity: velocity,
                scale: scale,
                isWalking: isWalking,
            };
            // console.log("Updating player state in GameScreen", data);
            controlsRef.current.updatePlayerState(data);
        }

        // --- Diamond pickup detection (client-side request, server validates) ---
        if (diamonds) {
            const now = Date.now();
            const cooldownMs = 800; // don't spam same id
            Object.values(diamonds).forEach(d => {
                if (!d || !Array.isArray(d.position)) return;
                if (pickupCooldownRef.current[d.id] && now - pickupCooldownRef.current[d.id] < cooldownMs) return;
                // Player body center and radii (mirror server)
                const scl = scale;
                const playerCenterY = (position[1] || 1) - 1 + 1.7 * scl;
                const rPlayer = Math.max(0.1, 0.2 * scl + 0.1);
                const rDiamond = 0.25;
                const dx = (position[0] || 0) - d.position[0];
                const dy = playerCenterY - (d.position[1] || 1.25);
                const dz = (position[2] || 0) - d.position[2];
                const sumR = rPlayer + rDiamond;
                const dist2 = dx*dx + dy*dy + dz*dz;
                if (dist2 <= sumR * sumR) {
                    sendData({ type: 'collectDiamond', payload: { id: d.id } });
                    pickupCooldownRef.current[d.id] = now;
                }
            });
        }

    }); // End of useFrame hook

    // --- JSX Rendering ---
    return (
        <>
            {/* Invisible collision/anchor mesh */}
            <mesh ref={vegetableMeshRef} visible={false} castShadow position={[0,0,0]} rotation={[0,0,0]}>
                <sphereGeometry args={[0.3, 16, 16]} />
                {/* Material doesn't matter much as it's invisible */}
                <meshStandardMaterial color={color} transparent opacity={0} />
            </mesh>

            {/* Local glow light based on collected diamonds */}
            <pointLight
                position={[position[0], position[1] + 0.8, position[2]]}
                color={'#ffffaa'}
                intensity={Math.min(2.5, computeCurrentGlow() * 0.25)}
                distance={6}
            />

            {/* Use the factory to get the appropriate visible character */}
            {/* Pass necessary state for animation and orientation */}
            <VegetableCharacterFactory
                vegetableType={vegetableType}
                position={position} // Use the calculated world position
                rotation={[0, characterRotation[1], 0]} // Use the character's facing rotation
                isWalking={isWalking}
                lookPitch={cameraPitch} // Pass camera pitch for eye movement
                scale={scale} // Pass scale for the model size
            />

            {/* Debug visualizers - Render based on 'debug' state */}
            {debug.colliding && debug.collisionPoint && (
                <group>
                    {/* Red sphere at collision point */}
                    <mesh position={debug.collisionPoint}>
                        <sphereGeometry args={[0.2, 8, 8]} />
                        <meshBasicMaterial color="red" wireframe />
                    </mesh>
                    {/* Yellow sphere showing plant collision radius (if applicable) */}
                    {debug.plantRadius && (
                        <mesh position={debug.collisionPoint}>
                            <sphereGeometry args={[debug.plantRadius, 16, 8]} />
                            <meshBasicMaterial color="yellow" wireframe opacity={0.5} transparent />
                        </mesh>
                    )}
                </group>
            )}
        </>
    );

} // End of VegetablePlayer component


// --- Scene component - Modified ---
// Accept world state and time config as props
function Scene({
    vegetableType,
    controlsRef,
    onGrow,
    plants, // Prop from backend
    walls,  // Prop from backend
    tree,   // Prop from backend
    otherPlayers,
    dayLengthMinutes, // Prop from backend
    initialTimeOfDay, // Prop from backend
    cycleEpochMs, // NEW: epoch for time sync
    invertY, // Prop for player controls
}) {
    const { diamonds } = useWebSocket();

    return (
        // Pass time config props to DayNightCycle
        <DayNightCycle dayLengthMinutes={dayLengthMinutes} initialTimeOfDay={initialTimeOfDay} cycleEpochMs={cycleEpochMs}>

                {/* Ground */}
                <Ground />

                {/* Walls - Render based on 'walls' prop */}
                {walls && walls.map((wall, index) => (
                    <Wall
                        key={`wall-${index}`}
                        // Adapt prop names if backend uses different casing (e.g., Position vs position)
                        position={wall.Position || wall.position}
                        size={wall.Size || wall.size}
                        rotation={wall.Rotation || wall.rotation || [0,0,0]} // Assume default rotation if missing
                    />
                ))}

                {/* Tree landmark - Render based on 'tree' prop */}
                {tree && (
                    <Tree
                        // Adapt prop names if backend uses different casing
                        position={tree.Position || tree.position}
                        // Pass other tree props if needed (e.g., scale, type)
                    />
                )}


                {/* Plants - Render based on 'plants' prop */}
                {plants && plants.map(plant => (
                    <Plant
                        // Use a unique ID from the backend if available, otherwise index
                        key={plant.ID || plant.id || `plant-${plant.Position?.join('-')}`}
                        // Adapt prop names if backend uses different casing
                        position={plant.Position || plant.position}
                        scale={plant.Scale || plant.scale}
                        type={plant.Type || plant.type || 'default'} // Handle type variations
                    />
                ))}

                {/* Diamonds - Render from server state */}
                {diamonds && Object.values(diamonds).map(d => (
                    <Diamond key={`diamond-${d.id}`} position={d.position} />
                ))}

                {/* Pickup effects on diamond collection */}
                <PickupEffects />

                {/* Player vegetable - Pass props down */}
                <VegetablePlayer
                    vegetableType={vegetableType}
                    controlsRef={controlsRef}
                    onCharacterGrow={onGrow} // Pass the growth callback
                    plants={plants} // Pass world objects for collision
                    walls={walls}
                    tree={tree}
                    invertY={invertY} // Pass invertY setting
                />

                {/* Render other players */}
                {otherPlayers && Object.entries(otherPlayers).map(([playerId, playerState]) => (
                    <OtherPlayer
                        key={`player-${playerId}`}
                        playerId={playerId}
                        playerState={playerState}
                    />
                ))}

        </DayNightCycle>
    );
}


// --- GardenScene3D component - Modified ---
// Accept props from GameScreen and pass them to Scene
const GardenScene3D = ({
    selectedVegetable,
    controlsRef,
    onGrow,
    otherPlayers, // Added prop
    plants,       // Added prop
    walls,        // Added prop
    tree,         // Added prop
    dayLengthMinutes, // Added prop
    initialTimeOfDay, // Added prop
    cycleEpochMs, // NEW prop
    invertY       // Added prop
}) => {
    return (
        <CanvasContainer>
            <FpsCounter /> {/* Outside the Canvas */}
            <Canvas shadows camera={{ position: [0, 3, 5], fov: 60 }}>
                <FpsMonitor /> {/* Inside the Canvas */}
                <Scene
                    vegetableType={selectedVegetable?.name || 'default'}
                    controlsRef={controlsRef}
                    onGrow={onGrow}
                    // Pass all the props down
                    plants={plants}
                    walls={walls}
                    tree={tree}
                    otherPlayers={otherPlayers}
                    dayLengthMinutes={dayLengthMinutes}
                    initialTimeOfDay={initialTimeOfDay}
                    cycleEpochMs={cycleEpochMs}
                    invertY={invertY}
                />
                {/* <OrbitControls /> */} {/* Keep for debugging if needed */}
            </Canvas>
        </CanvasContainer>
    );
};

export default GardenScene3D;
